
import { Industry } from "@/types/assessment";

/**
 * Calculates the market position score based on dimension scores and industry.
 */
export const calculateMarketPosition = (dimensionScores: Record<string, number>, industry: Industry): number => {
  const strategyWeight = 0.4;
  const innovationWeight = 0.4;
  const othersWeight = 0.2;
  
  const strategyScore = dimensionScores["strategy"] || 0;
  const innovationScore = dimensionScores["innovation"] || 0;
  const otherScores = Object.values(dimensionScores).reduce((sum, score) => sum + score, 0) / Object.values(dimensionScores).length;
  
  return parseFloat((strategyScore * strategyWeight + innovationScore * innovationWeight + otherScores * othersWeight).toFixed(1));
};

/**
 * Calculates the AI readiness score based on dimension scores.
 */
export const calculateAIReadiness = (dimensionScores: Record<string, number>): number => {
  const dataWeight = 0.35;
  const talentWeight = 0.35;
  const deploymentWeight = 0.3;
  
  const dataScore = dimensionScores["data"] || 0;
  const talentScore = dimensionScores["talent"] || 0;
  const deploymentScore = dimensionScores["deployment"] || 0;
  
  return parseFloat((dataScore * dataWeight + talentScore * talentWeight + deploymentScore * deploymentWeight).toFixed(1));
};

/**
 * Calculates the industry rank based on overall score and industry.
 */
export const calculateIndustryRank = (overallScore: number, industry: Industry): number => {
  const baseRank = Math.max(1, Math.min(100, Math.round(100 - (overallScore * 20))));
  
  const industryAdjustments: Partial<Record<Industry, number>> = {
    "Financial Services": -10,
    "Healthcare & Pharma": -5,
    "Education & EdTech": 10,
    "Government & Public Sector": 15
  };
  
  const adjustment = industryAdjustments[industry] || 0;
  return Math.max(1, Math.min(100, baseRank + adjustment));
};

/**
 * Determines the trend direction based on change value.
 */
export const determineTrend = (change?: number): "up" | "down" | "neutral" => {
  if (!change) return "neutral";
  return change > 0 ? "up" : change < 0 ? "down" : "neutral";
};

/**
 * Calculates the percentage change between current and previous values.
 */
export const calculatePercentageChange = (current: number, previous: number): number | undefined => {
  if (previous === 0) return undefined;
  return Math.round((current - previous) / previous * 100);
};
